import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/components/ui/shadcn/table';

// 🎯 Importaciones de la nueva estructura Clean Architecture
import {
  faArrowDown,
  faArrowUp,
  faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmptyState } from './components';
import {
  useContainerWidth,
  useInfiniteScroll,
  useSorting,
  useTableConfig,
} from './hooks';
import { TABLE_CONSTANTS, VirtualTableProps } from './types';

export const VirtualTable = React.memo(
  <T = any,>({
    data,
    columns,
    height = 'calc(100vh - 160px)',
    rowHeight = TABLE_CONSTANTS.DEFAULT_ROW_HEIGHT,
    overscan = TABLE_CONSTANTS.DEFAULT_OVERSCAN,
    className = '',
    emptyMessage,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage = false,
    infiniteScrollThreshold = TABLE_CONSTANTS.DEFAULT_SCROLL_THRESHOLD,
    autoHeight = false,
    maxHeight,
    minHeight = '0px',
    sorting,
    onSortingChange,
    disableHorizontalScroll = false,
  }: VirtualTableProps<T> & { disableHorizontalScroll?: boolean }) => {
    // 🎯 Hook de traducción para namespace UI compartido
    const { t } = useTranslation('ui');

    // 🎯 Mensaje vacío con fallback a traducción
    const finalEmptyMessage = emptyMessage || t('virtualTable.emptyMessage');

    // 🎯 Refs for DOM manipulation
    const parentRef = React.useRef<HTMLDivElement>(null);

    // 🎯 Custom hooks - Dependency Inversion Principle
    const { width: containerWidth } = useContainerWidth(parentRef);
    const tableConfig = useTableConfig(columns, containerWidth);
    const { handleSort, getSortIcon, isSortable } = useSorting({
      sorting,
      onSortingChange,
      columns,
    });

    // 🎯 Infinite scroll with object parameter for better maintainability
    useInfiniteScroll({
      parentRef,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      threshold: infiniteScrollThreshold,
    });

    // 🎯 Memoized height calculation for performance
    const calculateContainerHeight = React.useMemo(() => {
      if (!autoHeight) return height;

      const headerHeight = rowHeight;
      const dataRowsHeight = data.length * rowHeight;
      const loadingRowHeight = isFetchingNextPage ? rowHeight : 0;
      const borderAndPadding = 4;

      const totalContentHeight =
        headerHeight + dataRowsHeight + loadingRowHeight + borderAndPadding;
      const maxHeightValue = maxHeight || height;

      return `min(${totalContentHeight}px, ${maxHeightValue})`;
    }, [
      autoHeight,
      height,
      rowHeight,
      data.length,
      isFetchingNextPage,
      maxHeight,
    ]);

    // 🎯 React Table configuration
    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      enableColumnResizing: false,
      debugTable: false,
    });

    const { rows } = table.getRowModel();

    // 🎯 Virtual row configuration
    const rowVirtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => rowHeight,
      overscan,
      measureElement:
        typeof window !== 'undefined' && navigator.userAgent.includes('Firefox')
          ? undefined
          : (element) => element?.getBoundingClientRect().height ?? rowHeight,
    });

    // 🎯 Container height calculation
    const containerHeight = autoHeight
      ? `min(${rowHeight + data.length * rowHeight + (isFetchingNextPage ? rowHeight : 0) + 4}px, ${maxHeight || height})`
      : height;

    // 🎯 Scroll area height calculation
    const scrollAreaHeight = autoHeight
      ? `${rowHeight + data.length * rowHeight + (isFetchingNextPage ? rowHeight : 0) + 4}px`
      : height;

    // 🎯 Table body height
    const tableBodyHeight = autoHeight
      ? `${data.length * rowHeight + (isFetchingNextPage ? rowHeight : 0)}px`
      : `${rowVirtualizer.getTotalSize() + (isFetchingNextPage ? rowHeight : 0)}px`;

    // 🎯 Memoized styles for performance
    const containerStyles = React.useMemo(() => {
      // Si solo hay un elemento, no aplicar estilos de altura
      if (data.length === 1) {
        return {};
      }

      return {
        height: containerHeight,
        minHeight: autoHeight ? minHeight : undefined,
      };
    }, [containerHeight, autoHeight, minHeight, data.length]);

    // 🎯 Early return for empty state
    if (!data || data.length === 0) {
      return (
        <EmptyState
          message={finalEmptyMessage}
          height={autoHeight ? minHeight : height}
          className={className}
        />
      );
    }

    return (
      <div
        className={`w-full border border-card-border rounded-[10px] overflow-hidden ${className} max-w-[100%] flex flex-col`}
        style={containerStyles}
      >
        <div
          ref={parentRef}
          className={`flex-1 table-scrollbar-minimal scrollbar-gutter-stable max-w-full smooth-scroll touch-pan-x ${disableHorizontalScroll ? '' : 'overflow-auto'}`}
          style={
            disableHorizontalScroll
              ? { height: scrollAreaHeight, overflowX: 'hidden' }
              : { height: scrollAreaHeight }
          }
        >
          <table
            className="w-full table-fixed"
            style={{
              width: tableConfig.isMobile
                ? `${tableConfig.tableWidth}px`
                : tableConfig.tableWidth,
            }}
          >
            {/* 🎯 Table Header - Fijo */}
            <TableHeader className="sticky top-0 z-10 shadow-sm text-sm font-bold">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="flex w-full text-sm font-bold"
                  style={{
                    height: `${rowHeight}px`,
                  }}
                >
                  {headerGroup.headers.map((header, headerIndex) => {
                    const columnConfig = tableConfig.columns[headerIndex];
                    const columnWidth = tableConfig.isMobile
                      ? `${columnConfig.calculatedWidth}px`
                      : `${columnConfig.calculatedWidth}%`;

                    const columnField = (header.column.columnDef as any)
                      .accessorKey as string;
                    const canSort = isSortable(columnField);
                    const isCurrentlySorted = sorting?.field === columnField;

                    return (
                      <TableHead
                        key={header.id}
                        className={`text-foreground border-b border-card-border bg-body-bg flex items-center px-3 sm:px-4 ${canSort ? 'cursor-pointer transition-colors' : ''}`}
                        style={{
                          width: columnWidth,
                          height: `${rowHeight}px`,
                        }}
                        onClick={
                          canSort ? () => handleSort(columnField) : undefined
                        }
                      >
                        <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap flex items-center gap-2">
                          <span>
                            {/*title={
                              header.isPlaceholder
                                ? ''
                                : (header.column.columnDef as any).title ||
                                  (typeof header.column.columnDef.header ===
                                  'string'
                                    ? header.column.columnDef.header
                                    : undefined)
                            }*/}
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </span>
                          {canSort && isCurrentlySorted && (
                            <FontAwesomeIcon
                              icon={
                                sorting.direction === 'asc'
                                  ? faArrowUp
                                  : faArrowDown
                              }
                              className="text-sm opacity-70"
                            />
                          )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>

            {/* 🎯 Virtual Table Body */}
            <tbody
              className="relative"
              style={{
                height: tableBodyHeight,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) return null;

                return (
                  <TableRow
                    key={row.id}
                    className="hover:bg-muted/50 transition-colors absolute left-0 w-full flex border-b border-card-border last:border-b-0"
                    style={{
                      top: `${virtualRow.start}px`,
                      height: `${virtualRow.size}px`,
                    }}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const columnConfig = tableConfig.columns[cellIndex];
                      const columnWidth = tableConfig.isMobile
                        ? `${columnConfig.calculatedWidth}px`
                        : `${columnConfig.calculatedWidth}%`;

                      return (
                        <td
                          key={cell.id}
                          className="flex items-center px-3 sm:px-4"
                          style={{
                            width: columnWidth,
                            height: `${rowHeight}px`,
                            borderBottom:
                              virtualRow.index === data.length - 1
                                ? 'none'
                                : '1px solid var(--border)',
                          }}
                        >
                          <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </TableRow>
                );
              })}

              {/* 🎯 Loading indicator when fetching more data */}
              {isFetchingNextPage && (
                <TableRow
                  className="absolute left-0 w-full flex"
                  style={{
                    top: `${rowVirtualizer.getTotalSize()}px`,
                    height: `${rowHeight}px`,
                  }}
                >
                  <td
                    colSpan={columns.length}
                    className="w-full flex items-center justify-center text-sm"
                    style={{
                      height: `${rowHeight}px`,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin mr-2"
                    />
                    {t('virtualTable.loadingMore')}
                  </td>
                </TableRow>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);
